import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AssistantTestCampaignDto,
  AssistantTestMemberDto,
  AssistantTestNoteRequest,
  AssistantTestPurchaseDto,
  AssistantTestPurchaseRequest,
  AssistantTestPushRequest,
  AssistantTestPushTicketDto,
  AssistantTestReceiptDto,
  AssistantTestResetDto,
  AssistantTestResetRequest,
  AssistantTestRunDto,
  AssistantTestRunRequest,
} from '@signalvote/shared';
import { authService } from '../../services/authService';
import { apiJson, apiVoid, type ApiErrorBody } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';

const BASE = '/api/admin/assistant-test';

/** The server's own message, so a 409 like "no pending wake-ups" reads as is. */
const serverError = (fallback: string) => (body: ApiErrorBody) => new Error(body.error ?? body.message ?? fallback);

export function useAssistantTestCampaigns() {
  return useQuery({
    queryKey: queryKeys.assistantTest.campaigns(),
    queryFn: () => apiJson<AssistantTestCampaignDto[]>(`${BASE}/campaigns`, { error: 'Failed to fetch Scout campaigns' }),
    enabled: authService.isAuthenticated(),
  });
}

export function useAssistantTestMembers(campaignId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.assistantTest.members(campaignId ?? ''),
    queryFn: () =>
      apiJson<AssistantTestMemberDto[]>(`${BASE}/members?campaignId=${encodeURIComponent(campaignId!)}`, {
        error: 'Failed to fetch Scout members',
      }),
    enabled: authService.isAuthenticated() && !!campaignId,
  });
}

function useRefreshMembers() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: queryKeys.assistantTest.all });
}

export function useAssistantTestRun() {
  const refresh = useRefreshMembers();
  return useMutation({
    mutationFn: (body: AssistantTestRunRequest) =>
      apiJson<AssistantTestRunDto>(`${BASE}/runs`, { method: 'POST', json: body, error: serverError('Failed to start the run') }),
    onSuccess: refresh,
  });
}

export function useAssistantTestPush() {
  return useMutation({
    mutationFn: (body: AssistantTestPushRequest) =>
      apiJson<AssistantTestPushTicketDto[]>(`${BASE}/push`, { method: 'POST', json: body, error: serverError('Failed to send the push') }),
  });
}

export function useAssistantTestReceipts() {
  return useMutation({
    mutationFn: (ticketIds: string[]) =>
      apiJson<AssistantTestReceiptDto[]>(`${BASE}/push/receipts`, {
        method: 'POST',
        json: { ticketIds },
        error: serverError('Failed to read receipts'),
      }),
  });
}

export function useAssistantTestReset() {
  const refresh = useRefreshMembers();
  return useMutation({
    mutationFn: (body: AssistantTestResetRequest) =>
      apiJson<AssistantTestResetDto>(`${BASE}/reset`, { method: 'POST', json: body, error: serverError('Failed to reset') }),
    onSuccess: refresh,
  });
}

export function useAssistantTestEditNote() {
  const refresh = useRefreshMembers();
  return useMutation({
    mutationFn: ({ id, ...body }: AssistantTestNoteRequest & { id: string }) =>
      apiVoid(`${BASE}/notes/${encodeURIComponent(id)}`, { method: 'PUT', json: body, error: serverError('Failed to save the note') }),
    onSuccess: refresh,
  });
}

export function useAssistantTestDeleteNote() {
  const refresh = useRefreshMembers();
  return useMutation({
    mutationFn: ({ id, campaignId }: { id: string; campaignId: string }) =>
      apiVoid(`${BASE}/notes/${encodeURIComponent(id)}?campaignId=${encodeURIComponent(campaignId)}`, {
        method: 'DELETE',
        error: serverError('Failed to delete the note'),
      }),
    onSuccess: refresh,
  });
}

export function useAssistantTestPurchase() {
  const refresh = useRefreshMembers();
  return useMutation({
    mutationFn: (body: AssistantTestPurchaseRequest) =>
      apiJson<AssistantTestPurchaseDto>(`${BASE}/purchase`, { method: 'POST', json: body, error: serverError('Failed to simulate the purchase') }),
    onSuccess: refresh,
  });
}
